import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { renderToReactElement } from '@tiptap/static-renderer'
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Mention from "@tiptap/extension-mention";
import {
  IComment,
  IEditCommentRequest,
} from "Data/interfaces/Activities/IComments";
import { IMDTheme } from "Data/interfaces/MultiDomain/IMDTheme";
import { ActivitiesActions } from "Store/Activities/Activities.actions";
import { useTimeout } from "Context/TimeoutContext";
import { GetAvatar } from "Utils/generateThumbnail";
import AccessPermission from "Utils/AcessPermission";
import { dateAndTimeToString, formatDateFromNow } from "Utils/DateUtils";
import { Icon } from "Components/UI";
import SpinCustom from "Components/UI/SpinCustom";
import Tooltip from "Components/UI/Tooltip";
import InputTipTap, { IInputData } from "../InputTipTap";
import Input from "../Input";
import ActionButtons from "../ActionButtons";
import ImageStaticDisplay from "../InputTipTap/components/ImageDisplay/ImageStaticDisplay";
import { MentionUserStaticDisplay } from "../InputTipTap/components/MentionUserDisplay";
import { MentionActivityStaticDisplay } from "../InputTipTap/components/MentionActivityDisplay";
import {
  Wrapper,
  Container,
  Header,
  Content,
  OwnerBadge,
  ActionButton,
  InputContainer,
  EditedContainer,
} from "./styles";

interface ICard {
  activityId?: string;
  item: IComment;
  isOwner?: boolean;
  first?: boolean;
  last?: boolean;
  isResponsible?: boolean;
  loadingShareLink?: boolean;
  active?: boolean;
  theme?: IMDTheme;
  editLoading?: boolean;
  onCopyLink: (comment: IComment) => void;
  onDelete: (comment: IComment) => void;
  onScrollToEnd?: () => void;
}

const Card: FC<ICard> = ({
  activityId,
  item,
  isOwner,
  first,
  last,
  isResponsible,
  loadingShareLink,
  active,
  theme,
  editLoading,
  onCopyLink,
  onDelete,
  onScrollToEnd,
}) => {
  const dispatch = useDispatch();
  const { set, clear } = useTimeout();

  const descriptionRef = useRef<HTMLSpanElement | null>(null);
  const [openString, setOpenString] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [activeUpdate, setActiveUpdate] = useState(false);

  const handleEdit = () => {
    setActiveUpdate(true);
    if (last && onScrollToEnd) {
      clear('Card-handleEdit');
      set('Card-handleEdit', () => {
        onScrollToEnd();
      }, 200);
    }
  };

  const handleSubmit = (data: IInputData) => {
    if (!activityId) return;

    const request: IEditCommentRequest = {
      topicId: activityId,
      commentId: item.Id,
      data: { commentText: data.text },
    };
    dispatch(ActivitiesActions.editComment(request));
  };

  const content = useMemo(() => {
    try {
      return {
        content: JSON.parse(item.CommentText),
        isString: false
      };
    } catch (error) {
      return {
        content: item.CommentText,
        isString: true
      };
    }
  }, [item.CommentText]);

  const renderTipTapContent = useCallback((content: any) => {
    return renderToReactElement({
      content: content,
      extensions: [
        StarterKit,
        Image.configure({ allowBase64: true }),
        Mention.configure({
          HTMLAttributes: {
            class: 'mention-user',
          },
        }).extend({
          name: 'mentionUser',
        }),
        Mention.configure({
          HTMLAttributes: {
            class: 'mention-activity',
          },
        }).extend({
          name: 'mentionActivity',
        }),
      ],
      options: {
        nodeMapping: {
          image: ({ node }: any) => {
            return (
              <ImageStaticDisplay key={node.attrs?.src} node={node} />
            )
          },
          mentionUser: ({ node }: any) => {
            return (
              <MentionUserStaticDisplay key={node.attrs?.id} node={node} />
            )
          },
          mentionActivity: ({ node }: any) => {
            return (
              <MentionActivityStaticDisplay key={node.attrs?.id} node={node} />
            )
          }
        },
      },
    });
  }, []);

  useEffect(() => {
    if (!activeUpdate && descriptionRef.current) {
      const { scrollHeight } = descriptionRef.current;
      setIsTruncated(scrollHeight > 126);
      setOpenString(false);
    }

    return () => clear('Card-handleEdit');
  }, [content, activeUpdate, clear]);

  return (
    <Wrapper first={first}>
      <Container active={active} isupdate={activeUpdate}>
        <Header>
          <span className="headerText">
            <span className="headerLeftText">
              <GetAvatar
                src={item.CreationAuthor.OriginalImageUrl}
                thumbSrc={item.CreationAuthor.ThumbnailImageUrl}
                name={item.CreationAuthor?.Nome || ''}
                size={24}
                fontSize={12}
              />
              <span className="name">{item.CreationAuthor.Nome || ''}</span>
            </span>
            {isResponsible && <OwnerBadge>Responsável</OwnerBadge>}
          </span>
          <span className="actionsContainer">
            {!!item.UpdateDate && (
              <Tooltip
                placement="top"
                title={`Editado em: ${dateAndTimeToString(item.UpdateDate)}`}
                arrow={false}
              >
                <EditedContainer>
                  <Icon
                    icon="pencil"
                    customSize={12}
                    className="editedIcon"
                  />
                  <span className="editedText">Editado</span>
                </EditedContainer>
              </Tooltip>
            )}
            <span className="date">
              {formatDateFromNow(item.DateTimeFormated)}
            </span>
            <span className="actions">
              <ActionButton onClick={() => onCopyLink(item)}>
                {loadingShareLink && (
                  <SpinCustom
                    size={10}
                    color={theme?.colors.surface.onSurfaceVariant}
                  />
                )}
                {!loadingShareLink && (
                  <Icon
                    icon="link"
                    customSize={12}
                    className="iconCopyLink"
                  />
                )}
              </ActionButton>
              {isOwner && (
                <ActionButton onClick={handleEdit}>
                  <Icon
                    icon="editar"
                    customSize={12}
                    className="iconEdit"
                  />
                </ActionButton>
              )}
              <ActionButtons
                loadingShareLink={loadingShareLink}
                hasDeletePermission={isOwner || AccessPermission.isAdmin()}
                hasEditPermission={isOwner}
                onCopyLink={() => onCopyLink(item)}
                onEdit={handleEdit}
                onDelete={() => onDelete(item)}
              />
            </span>
          </span>
        </Header>
        <Content open={openString || (activeUpdate && !content.isString)}>
          {!activeUpdate && (
            <>
              <span ref={descriptionRef} className="description">
                {content.isString && (
                  content.content
                )}
                {!content.isString && (
                  renderTipTapContent(content.content)
                )}
              </span>
              {isTruncated && (
                <span
                  className="seeMore"
                  onClick={() => setOpenString(prev => !prev)}
                >
                  {openString ? 'Ver menos' : 'Ver mais'}
                </span>
              )}
            </>
          )}
          {activeUpdate && (
            <InputContainer>
              {content.isString && (
                <Input
                  loading={editLoading}
                  isUpdate
                  defaultValue={content.content}
                  onSubmit={handleSubmit}
                  onActiveCallback={setActiveUpdate}
                />
              )}
              {!content.isString && (
                <InputTipTap
                  loading={editLoading}
                  isUpdate
                  defaultValue={content.content}
                  onSubmit={handleSubmit}
                  onActiveCallback={setActiveUpdate}
                />
              )}
            </InputContainer>
          )}
        </Content>
      </Container>
    </Wrapper>
  )
}

export default Card;
